import React, { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import {
  dbTrue,
  fetchZohoRecords,
  type ZohoGstinDTO,
  type ZohoOrganisationDTO,
} from "../../data/zohoRecordsApi";
import { Callout } from "../../components/Callout";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import { useSubStepNav } from "../useSubStepNav";

const ANSWER_KEY = "verifySetup.gstReview";

type RegistrationType = "regular" | "composition" | "sez";
type FilingFrequency = "monthly" | "qrmp" | "composition";

/**
 * What the CA says about each registration, once they have looked at it.
 *
 * Only filing frequency. Registration type and e-invoicing are shown as Zoho
 * holds them and cannot be edited here — neither is writable through the API,
 * so a control offering to change them would have been a control that lies.
 *
 * Keyed by GSTIN rather than by position: the branch list is re-read on every
 * connection refresh, and a client who adds a branch would otherwise have
 * every answer after it shifted onto the wrong registration.
 */
interface GstReviewAnswer {
  filingFrequency?: FilingFrequency;
}

type GstReviewDraft = Record<string, GstReviewAnswer>;

const REGISTRATION_TYPES: { value: RegistrationType; label: string }[] = [
  { value: "regular", label: "Regular" },
  { value: "composition", label: "Composition" },
  { value: "sez", label: "SEZ" },
];

const EINVOICING: { value: string; label: string }[] = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];

/**
 * How often this registration files, which is a Zournal answer and only ever a
 * Zournal answer: no Zoho endpoint reads or writes it.
 *
 * Three, because three is what the GST law offers a registered person:
 *
 *   Monthly      GSTR-1 + GSTR-3B every month. Compulsory above ₹5 crore
 *                aggregate turnover, and the default for everyone else who has
 *                not opted out.
 *   QRMP         Quarterly Return, Monthly Payment. Available at or below
 *                ₹5 crore. Returns quarterly, tax still paid every month on
 *                PMT-06, so "quarterly" on its own is the wrong word for it.
 *   Composition  A different return series altogether — CMP-08 each quarter and
 *                GSTR-4 once a year. Not a slower version of the other two.
 *
 * Which of them can apply is decided by the registration type, so the options
 * are filtered rather than all three being offered and two of them being wrong.
 */
const FILING_FREQUENCIES: {
  value: FilingFrequency;
  label: string;
  appliesTo: RegistrationType[];
}[] = [
  { value: "monthly", label: "Monthly", appliesTo: ["regular", "sez"] },
  { value: "qrmp", label: "QRMP (quarterly)", appliesTo: ["regular", "sez"] },
  { value: "composition", label: "Composition (CMP-08, quarterly)", appliesTo: ["composition"] },
];

function frequenciesFor(type: RegistrationType | undefined): typeof FILING_FREQUENCIES {
  // Nothing read from Zoho means nothing to filter on, so offer the lot rather
  // than guessing and hiding the one they need.
  if (type === undefined) return FILING_FREQUENCIES;
  return FILING_FREQUENCIES.filter((option) => option.appliesTo.includes(type));
}

/**
 * The frequency to show before anybody has chosen one.
 *
 * Deliberately not written to the draft on load. A stored answer means "a CA
 * looked at this and said so"; seeding it on render would make every
 * registration claim to have been reviewed the moment the page opened. Read it
 * through this function instead, so the default lives in one place and the
 * absence of an answer stays visible to whatever comes later.
 */
function defaultFrequency(type: RegistrationType | undefined): FilingFrequency {
  return type === "composition" ? "composition" : "monthly";
}

/**
 * What Zoho says the registration type is.
 *
 * Zoho carries composition and SEZ as flags on the organisation and exposes
 * nothing per registration. Fine while we build for one GSTIN per client; the
 * day that changes, this is the line that has to.
 */
function zohoRegistrationType(org: ZohoOrganisationDTO | null): RegistrationType | undefined {
  if (org === null) return undefined;
  if (dbTrue(org.is_sez)) return "sez";
  if (dbTrue(org.is_composition_scheme)) return "composition";
  if (dbTrue(org.is_gst_registered)) return "regular";
  return undefined;
}

const SELECT_CLASS = "h-9 rounded-md border bg-background px-2 text-sm";

/**
 * A Zoho-owned setting: open the dropdown and every option is there, but only
 * the one Zoho holds can be chosen.
 *
 * THE SELECT ITSELF IS NOT DISABLED, and that is the whole point. A disabled
 * select cannot be opened at all, so its options are invisible — which defeats
 * the reason for using a dropdown rather than a line of text. "Regular" on its
 * own does not say what else it could have been, and this screen exists for a
 * CA to judge whether what Zoho holds is right.
 *
 * So the options carry the `disabled` instead. The browser renders the full
 * list, greys out the ones that are not the current value, and refuses to
 * select them — enforcement by the platform rather than by a handler that
 * quietly undoes the change after the fact, which looks broken. Arrow keys skip
 * disabled options too, so the keyboard path behaves the same as the mouse one.
 *
 * `onChange` is a no-op belt to that braces: the value is controlled and never
 * moves, so even a browser that let an option through would change nothing.
 * Enabled also means it stays in the tab order, which a disabled control would
 * not.
 */
function ReadOnlySelect({
  label,
  value,
  options,
  unknownLabel,
}: {
  label: string;
  value: string | undefined;
  options: { value: string; label: string }[];
  unknownLabel: string;
}) {
  const known = value !== undefined;
  const current = known ? value : "__unknown";

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <select
        value={current}
        onChange={() => {}}
        aria-readonly
        // Answers "why can't I pick one?" at the moment somebody tries, without
        // putting a line of explanatory copy on the screen for everyone else.
        title="Set in Zoho Books - this page cannot change it."
        className={cn(
          SELECT_CLASS,
          // The muted fill is what marks it read-only, now that being greyed
          // out is no longer available to do that job.
          "bg-muted/40",
          !known && "italic text-muted-foreground"
        )}
      >
        {/*
          Only present when there is nothing to select, so "not set" never
          appears as something a value could be changed to.
        */}
        {!known && <option value="__unknown">{unknownLabel}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.value !== current}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function GstinCard({
  gstin,
  answer,
  zohoType,
  onChange,
}: {
  gstin: ZohoGstinDTO;
  answer: GstReviewAnswer;
  zohoType: RegistrationType | undefined;
  onChange: (patch: GstReviewAnswer) => void;
}) {
  // Null reads as OFF, deliberately, and it is null more often than it looks.
  // Zoho answers e-invoicing for the whole organisation, but an organisation
  // with Locations switched on gets a stripped payload naming no registration,
  // so `applyEinvoicing()` has nothing to stamp the answer onto and every row
  // stays null — on a client whose books plainly said the feature is off.
  // Showing "could not read" there sent a CA looking for a failure that had
  // not happened.
  //
  // This is a display default, not a claim that we read it: the column is
  // still null, nothing backfills it, and this flag drives nothing outside
  // this screen and the month-close checklist. The cost is that a genuinely
  // failed read now also shows "off" — accepted knowingly, because the flag's
  // blast radius is those two screens. If it ever starts driving tenant
  // behaviour, this default has to come back out.
  const zohoEinvoice = dbTrue(gstin.is_einvoice_enabled);

  const options = frequenciesFor(zohoType);
  const frequency = answer.filingFrequency ?? defaultFrequency(zohoType);

  return (
    <li className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-mono font-medium">{gstin.gstin ?? "No GSTIN on this branch"}</span>
        <span className="text-xs text-muted-foreground">
          {[gstin.branch_name, gstin.state].filter(Boolean).join(" · ")}
          {dbTrue(gstin.is_primary) && " · primary"}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <ReadOnlySelect
          label="Registration type"
          value={zohoType}
          options={REGISTRATION_TYPES}
          unknownLabel="not set"
        />

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Filing frequency</span>
          <select
            value={frequency}
            onChange={(event) => onChange({ filingFrequency: event.target.value as FilingFrequency })}
            className={SELECT_CLASS}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* `unknownLabel` is unreachable here — `zohoEinvoice` is a boolean now,
            so `value` is never undefined and the unknown option never renders.
            It stays only because the prop is required and the registration-type
            select above genuinely needs it; a signature change to delete one
            dead string is not worth it. Nothing on screen can show this text. */}
        <ReadOnlySelect
          label="e-invoicing"
          value={zohoEinvoice ? "on" : "off"}
          options={EINVOICING}
          unknownLabel="could not read"
        />
      </div>

      {gstin.einvoice_username && (
        <p className="text-xs text-muted-foreground">
          IRP login {gstin.einvoice_username}
          {gstin.einvoice_portal_name ? ` via ${gstin.einvoice_portal_name}` : ""}.
        </p>
      )}
    </li>
  );
}

export function GstReviewStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "gst-review");
  const draft = useCoAnswer<GstReviewDraft>(clientId, ANSWER_KEY) ?? {};

  const [gstins, setGstins] = useState<ZohoGstinDTO[] | null>(null);
  const [organisation, setOrganisation] = useState<ZohoOrganisationDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoadError(null);

    fetchZohoRecords(clientId, ["gstins", "organisation"])
      .then((records) => {
        if (!live) return;
        setGstins(records.gstins ?? []);
        setOrganisation(records.organisation ?? null);
      })
      .catch((error: unknown) => {
        if (live) setLoadError(error instanceof Error ? error.message : "Could not load the GST registrations.");
      });

    return () => {
      live = false;
    };
  }, [clientId]);

  if (!client) return null;

  function patch(gstinKey: string, changes: GstReviewAnswer) {
    void persistAnswer<GstReviewDraft>(clientId, ANSWER_KEY, {
      ...draft,
      [gstinKey]: { ...draft[gstinKey], ...changes },
    });
  }

  const clientName = client.name || "this client";
  const zohoType = zohoRegistrationType(organisation);
  const registered = organisation === null || dbTrue(organisation.is_gst_registered);

  // Nothing to review yet is different from nothing to review at all: a read
  // still in flight or one that failed blocks the sign-off, while "no
  // registrations" is a legitimate answer that lets the flow move on.
  const reviewable = loadError === null && gstins !== null;

  async function confirmAndContinue() {
    // Continue is the review sign-off. Record the frequency actually shown for
    // any registration the CA left on its default, so downstream readers see a
    // confirmed answer rather than a gap.
    if (registered && gstins !== null && gstins.length > 0) {
      const next: GstReviewDraft = { ...draft };
      let changed = false;
      for (const gstin of gstins) {
        const key = gstin.gstin ?? gstin.branch_id;
        if (next[key]?.filingFrequency === undefined) {
          next[key] = { ...next[key], filingFrequency: defaultFrequency(zohoType) };
          changed = true;
        }
      }
      if (changed) await persistAnswer<GstReviewDraft>(clientId, ANSWER_KEY, next);
    }
    await goNext();
  }

  return (
    <SubStepShell
      title="GST Settings"
      thenWhat={reviewable ? "Continue → TDS & TCS Settings." : "The registrations have to load before this step can be signed off."}
      onContinue={() => void confirmAndContinue()}
      continueDisabled={!reviewable}
      description={
        gstins !== null && gstins.length === 0
          ? `${clientName}'s books show no GST registrations.`
          : `Below are the GST settings pulled from ${clientName}'s Zoho Books account. Select the GST Filing Frequency so Zournal can track the client's GST compliance schedule.`
      }
    >
      {loadError !== null ? (
        <Callout variant="warning">{loadError}</Callout>
      ) : gstins === null ? (
        <p className="text-sm text-muted-foreground">Reading the GST registrations…</p>
      ) : !registered ? (
        <Callout variant="warning">
          Zoho says {clientName} is not registered for GST. Nothing to review here. If that is wrong, fix it
          in Zoho Books and re-run the read on Confirm Connection to Zoho.
        </Callout>
      ) : gstins.length === 0 ? (
        <Callout variant="warning">
          No GST registrations were read from the books. If {clientName} has one, fix it in Zoho Books and
          re-run the read on Confirm Connection to Zoho.
        </Callout>
      ) : (
        <ul className="flex flex-col gap-4">
          {gstins.map((gstin) => {
            const key = gstin.gstin ?? gstin.branch_id;
            return (
              <GstinCard
                key={gstin.branch_id}
                gstin={gstin}
                answer={draft[key] ?? {}}
                zohoType={zohoType}
                onChange={(changes) => patch(key, changes)}
              />
            );
          })}
        </ul>
      )}
    </SubStepShell>
  );
}
